import { type JSX } from 'react';

import { type AdfoxContextProviderProps, AdfoxTGBBanner } from '@/packages/adfox';
import { AdfoxParallaxBanner } from '@/packages/adfox/AdfoxParallaxBanner/AdfoxParallaxBanner';
import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { isLastListItem } from '@/packages/utils/array';

import { type IOfferForDesktopEnrichedV3Schema } from '@/repositories/generated/shared/search-offers/entities/offer_for_desktop/OfferForDesktopEnrichedV3Schema';

import { AdfoxParallaxBannerWrapper } from '../components/AdfoxParallaxBannerWrapper';
import { AdfoxTgbBannerWrapper } from '../components/AdfoxTgbBannerWrapper';
import { type TCardApiContext } from '../types/TCardApiContext';
import { type TCardContextData } from '../types/TCardContextData';

import { CardContainer } from './CardContainer';

type TCardsWithAdsContainerProps = {
  adfox: AdfoxContextProviderProps['serverData'];
  dealType: 'sale' | 'rent';
  offers: IOfferForDesktopEnrichedV3Schema[];
  initCardApi: (cardData: TCardContextData) => TCardApiContext;
  renderAdvertisment?: (props: { index: number; previousOfferId: number }) => JSX.Element;
};

export const CardsWithAdsContainer = memoWithDisplayName<TCardsWithAdsContainerProps>(
  ({ dealType, offers, adfox, initCardApi }) => {
    return (
      <>
        {offers.reduce((accumulator, offer, index, offersArr) => {
          accumulator.push(
            <CardContainer dealType={dealType} index={index} initCardApi={initCardApi} key={offer.id} offer={offer} />,
          );

          if (isLastListItem(offersArr, index)) {
            return accumulator;
          }

          /**
           * @description каждое 10 объявление: 10, 20, 30, ...
           */
          if ((index + 1) % 10 === 0) {
            accumulator.push(
              <AdfoxParallaxBannerWrapper key={`adfox_parallax_banner_next_to_${index + 1}`}>
                <AdfoxParallaxBanner position={index + 1} serverData={adfox} />
              </AdfoxParallaxBannerWrapper>,
            );
            /**
             * @description каждое 10 объявление если начинать с 4: 4, 14, 24, ...
             */
          } else if ((index - 3) % 10 === 0) {
            accumulator.push(
              <AdfoxTgbBannerWrapper key={`adfox_tgb_banner_next_to_${index + 1}`}>
                <AdfoxTGBBanner position={index + 1} serverData={adfox} />
              </AdfoxTgbBannerWrapper>,
            );
          }

          return accumulator;
        }, Array.of<JSX.Element>())}
      </>
    );
  },
  'Cards',
);
